import React, { useState } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  ShieldCheck,
  AlertTriangle,
  Search,
  Filter,
  Download,
  Plus,
  Eye,
  Calendar,
  FileText,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { toast } from 'sonner';

export default function LicenseManagementPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Mock license data
  const licenses = [
    {
      id: 1,
      licenseNumber: 'CL-2024-001',
      type: 'Cultivation',
      operator: 'Green Valley Farms',
      status: 'Active',
      issueDate: '2024-01-15',
      expiryDate: '2025-01-15',
      daysUntilExpiry: 198,
      state: 'California',
      documents: ['License Certificate', 'Insurance Proof', 'Inspection Report']
    },
    {
      id: 2,
      licenseNumber: 'DL-2024-045',
      type: 'Dispensary',
      operator: 'Springfield Wellness',
      status: 'Expiring Soon',
      issueDate: '2023-07-20',
      expiryDate: '2024-12-20',
      daysUntilExpiry: 15,
      state: 'Colorado',
      documents: ['License Certificate', 'Insurance Proof']
    },
    {
      id: 3,
      licenseNumber: 'ML-2024-089',
      type: 'Manufacturing',
      operator: 'Pure Extracts LLC',
      status: 'Active',
      issueDate: '2024-03-10',
      expiryDate: '2025-03-10',
      daysUntilExpiry: 253,
      state: 'Oregon',
      documents: ['License Certificate', 'Safety Certification', 'Insurance Proof']
    },
    {
      id: 4,
      licenseNumber: 'TL-2024-023',
      type: 'Testing Lab',
      operator: 'CannaTest Analytics',
      status: 'Active',
      issueDate: '2024-02-05',
      expiryDate: '2025-02-05',
      daysUntilExpiry: 228,
      state: 'Washington',
      documents: ['License Certificate', 'Lab Accreditation', 'Insurance Proof']
    },
    {
      id: 5,
      licenseNumber: 'DL-2023-156',
      type: 'Dispensary',
      operator: 'Urban Leaf Collective',
      status: 'Expired',
      issueDate: '2023-01-10',
      expiryDate: '2024-11-10',
      daysUntilExpiry: -25,
      state: 'Nevada',
      documents: ['License Certificate']
    },
    {
      id: 6,
      licenseNumber: 'CL-2024-078',
      type: 'Cultivation',
      operator: 'Mountain View Grow',
      status: 'Pending Renewal',
      issueDate: '2023-09-15',
      expiryDate: '2024-12-25',
      daysUntilExpiry: 20,
      state: 'California',
      documents: ['License Certificate', 'Renewal Application']
    },
    {
      id: 7,
      licenseNumber: 'DL-2024-192',
      type: 'Delivery',
      operator: 'Quick Green Delivery',
      status: 'Active',
      issueDate: '2024-05-20',
      expiryDate: '2025-05-20',
      daysUntilExpiry: 328,
      state: 'California',
      documents: ['License Certificate', 'Vehicle Registration', 'Insurance Proof']
    },
    {
      id: 8,
      licenseNumber: 'ML-2024-034',
      type: 'Manufacturing',
      operator: 'Edible Delights Co',
      status: 'Active',
      issueDate: '2024-04-01',
      expiryDate: '2025-04-01',
      daysUntilExpiry: 275,
      state: 'Colorado',
      documents: ['License Certificate', 'Health Permit', 'Insurance Proof']
    }
  ];

  const filteredLicenses = licenses.filter(license =>
    license.licenseNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
    license.operator.toLowerCase().includes(searchTerm.toLowerCase()) ||
    license.type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusBadge = (status, daysUntilExpiry) => {
    if (status === 'Expired' || daysUntilExpiry < 0) {
      return <Badge variant="destructive" className="flex items-center gap-1"><XCircle className="h-3 w-3" />Expired</Badge>;
    }
    if (status === 'Expiring Soon' || daysUntilExpiry <= 30) {
      return <Badge className="bg-orange-500 text-white flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Expiring Soon</Badge>;
    }
    if (status === 'Pending Renewal') {
      return <Badge className="bg-yellow-500 text-white flex items-center gap-1"><Calendar className="h-3 w-3" />Pending Renewal</Badge>;
    }
    return <Badge variant="success" className="flex items-center gap-1"><CheckCircle className="h-3 w-3" />Active</Badge>;
  };

  const summaryCards = [
    {
      title: 'Total Licenses',
      value: licenses.length,
      icon: ShieldCheck,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      title: 'Active Licenses',
      value: licenses.filter(l => l.status === 'Active').length,
      icon: CheckCircle,
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      title: 'Expiring Soon',
      value: licenses.filter(l => l.daysUntilExpiry <= 30 && l.daysUntilExpiry > 0).length,
      icon: AlertTriangle,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    },
    {
      title: 'Expired',
      value: licenses.filter(l => l.daysUntilExpiry < 0).length,
      icon: XCircle,
      color: 'text-red-600',
      bgColor: 'bg-red-50'
    },
  ];

  const viewLicenseDetails = (license) => {
    setSelectedLicense(license);
    setIsDialogOpen(true);
  };

  const handleExport = () => {
    toast.success('Exporting license data to Excel...');
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">License Management</h1>
            <p className="text-muted-foreground mt-1">Track and manage all operator licenses</p>
          </div>
          <Button className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Add License
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {summaryCards.map((card, index) => (
            <Card key={index}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{card.title}</p>
                    <p className="text-3xl font-bold mt-2">{card.value}</p>
                  </div>
                  <div className={`p-3 ${card.bgColor} rounded-lg`}>
                    <card.icon className={`h-6 w-6 ${card.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* License Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>All Licenses</CardTitle>
                <CardDescription>Complete list of operator licenses</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleExport}>
                  <Download className="h-4 w-4 mr-2" />
                  Export
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Search Bar */}
            <div className="flex items-center gap-2 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by license number, operator, or type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            {/* Table */}
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>License Number</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Operator</TableHead>
                    <TableHead>State</TableHead>
                    <TableHead>Issue Date</TableHead>
                    <TableHead>Expiry Date</TableHead>
                    <TableHead>Days Until Expiry</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLicenses.length > 0 ? (
                    filteredLicenses.map((license) => (
                      <TableRow key={license.id}>
                        <TableCell className="font-medium">{license.licenseNumber}</TableCell>
                        <TableCell>{license.type}</TableCell>
                        <TableCell>{license.operator}</TableCell>
                        <TableCell>{license.state}</TableCell>
                        <TableCell>{license.issueDate}</TableCell>
                        <TableCell>{license.expiryDate}</TableCell>
                        <TableCell>
                          <span className={license.daysUntilExpiry <= 30 ? 'text-red-600 font-medium' : ''}>
                            {license.daysUntilExpiry > 0 ? `${license.daysUntilExpiry} days` : 'Expired'}
                          </span>
                        </TableCell>
                        <TableCell>{getStatusBadge(license.status, license.daysUntilExpiry)}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => viewLicenseDetails(license)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-muted-foreground">
                        No licenses found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* License Details Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>License Details</DialogTitle>
            <DialogDescription>Complete information about the license</DialogDescription>
          </DialogHeader>
          {selectedLicense && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">License Number</p>
                  <p className="font-medium">{selectedLicense.licenseNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Type</p>
                  <p className="font-medium">{selectedLicense.type}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Operator</p>
                  <p className="font-medium">{selectedLicense.operator}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">State</p>
                  <p className="font-medium">{selectedLicense.state}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Issue Date</p>
                  <p className="font-medium">{selectedLicense.issueDate}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Expiry Date</p>
                  <p className="font-medium">{selectedLicense.expiryDate}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Days Until Expiry</p>
                  <p className="font-medium">
                    {selectedLicense.daysUntilExpiry > 0 ? `${selectedLicense.daysUntilExpiry} days` : 'Expired'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <div className="mt-1">{getStatusBadge(selectedLicense.status, selectedLicense.daysUntilExpiry)}</div>
                </div>
              </div>

              <div>
                <p className="text-sm text-muted-foreground mb-2">Documents</p>
                <div className="space-y-2">
                  {selectedLicense.documents.map((doc, index) => (
                    <div key={index} className="flex items-center gap-2 p-2 border rounded-lg">
                      <FileText className="h-4 w-4 text-primary" />
                      <span className="text-sm">{doc}</span>
                      <Button variant="ghost" size="sm" className="ml-auto">
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-2">
                <Button className="flex-1">Renew License</Button>
                <Button variant="outline" className="flex-1">Edit Details</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
